import { AuthError, InteractionType } from '@azure/msal-browser';
import { MsalAuthenticationResult, MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { isRouteErrorResponse, Navigate, useRouteError } from 'react-router-dom';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { authRequest } from './authConfig';
import { AuthenticatedElementProps } from './types';
import BlockUISpinner from '../components/BlockUISpinner';
import PreConditions from '../routes/preConditions/PreConditions';
import ErrorBoundary from '../components/ErrorBoundary';
import { ai } from '../instrumentation/AppInsightsService';
import AppLogger from '../instrumentation/AppLogger';
import { SeverityLevel } from '@microsoft/applicationinsights-common';

const Loading = () => <BlockUISpinner><p>Logging in...</p></BlockUISpinner>;

const RedirectToHome: React.FC<MsalAuthenticationResult> = ({error}) => {
    const { instance } = useMsal();
    const routerError = useRouteError();
    if (isRouteErrorResponse(routerError)) {
        AppLogger.trace(`Router error ${routerError.statusText}`, SeverityLevel.Error);
        instance.setActiveAccount(null);
        return routerError.status === 404 ? <Navigate to='/not-found' /> : <Navigate to='/server-error' />;
    }

    AppLogger.error('MSAL Error', error as Error);

    instance.setActiveAccount(null);
    return <Navigate to='/' />;
};

const AuthenticatedElement = ({ children, displayHeaderAndFooter = true }: AuthenticatedElementProps) => (
    <PreConditions displayHeaderAndFooter={displayHeaderAndFooter}>
        <MsalAuthenticationTemplate
            interactionType={InteractionType.Redirect}
            authenticationRequest={authRequest}
            loadingComponent={Loading}
            errorComponent={RedirectToHome}
        >
            <ErrorBoundary appInsights={ai.reactPlugin as ReactPlugin}>
                {children}
            </ErrorBoundary>
        </MsalAuthenticationTemplate>
    </PreConditions>
);

export default AuthenticatedElement;
